import type { CompanySettings } from '@crewcast/shared';

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt: number; // Timestamp when token expires
}

export interface User {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'admin' | 'employee' | 'superadmin';
  companyId: string;
  companyName?: string;  // Resolved display name from the Companies table
  status: string;
  points?: number;
  profilePicture?: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  hasAcceptedContentRights?: boolean;
  contentRightsAcceptedAt?: string;
  companySettings?: CompanySettings;
}

const TOKEN_KEY = 'auth_tokens';
const REFRESH_COOKIE_NAME = 'cc_refresh';
const USER_KEY = 'user_data';
const LAST_ACTIVITY_KEY = 'last_activity';
const INACTIVITY_DAYS = 7;
const REFRESH_TOKEN_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

const cookieManager = {
  set(name: string, value: string, maxAge: number): void {
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; Secure; SameSite=Strict`;
  },
  get(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  },
  delete(name: string): void {
    document.cookie = `${name}=; max-age=0; path=/; Secure; SameSite=Strict`;
  },
};

/**
 * Token management utilities
 */
export const tokenManager = {
  saveTokens(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
    cookieManager.set(REFRESH_COOKIE_NAME, tokens.refreshToken, REFRESH_TOKEN_MAX_AGE);
    this.updateActivity();
  },

  getTokens(): AuthTokens | null {
    const tokens = localStorage.getItem(TOKEN_KEY);
    if (!tokens) return null;

    let parsedTokens: AuthTokens;
    try {
      parsedTokens = JSON.parse(tokens);
    } catch {
      // Corrupt or legacy-format token data — discard rather than crash on boot.
      this.clearTokens();
      return null;
    }

    // Inactivity clears everything including the refresh token
    if (this.isInactive()) {
      this.clearTokens();
      return null;
    }

    // Expired access tokens are expected — the refresh mechanism handles them.
    // Don't clear here or we wipe the refresh token before it can be used.
    if (this.isExpired(parsedTokens)) return null;

    return parsedTokens;
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    cookieManager.delete(REFRESH_COOKIE_NAME);
  },

  getAccessToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.accessToken || null;
  },

  getIdToken(): string | null {
    const tokens = this.getTokens();
    return tokens?.idToken || null;
  },

  getRefreshToken(): string | null {
    // Read from a persistent cookie so the refresh token survives browser restarts.
    // Bypasses getTokens() intentionally — the access token expiring doesn't mean
    // the refresh token is invalid (it has its own 30-day Cognito lifetime).
    if (this.isInactive()) return null;
    return cookieManager.get(REFRESH_COOKIE_NAME);
  },

  isAuthenticated(): boolean {
    const tokens = this.getTokens();
    return !!tokens && !this.isExpired(tokens) && !this.isInactive();
  },

  isExpired(tokens: AuthTokens): boolean {
    if (!tokens.expiresAt) {
      return true;
    }
    // Add 5 minute buffer before actual expiration
    return Date.now() >= (tokens.expiresAt - 300000);
  },

  isInactive(): boolean {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivity) return false;
    
    const lastActivityTime = parseInt(lastActivity, 10);
    const inactivityMs = INACTIVITY_DAYS * 24 * 60 * 60 * 1000; // 7 days in ms
    
    return Date.now() - lastActivityTime > inactivityMs;
  },

  updateActivity(): void {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  },

  getTimeUntilExpiration(): number {
    const tokens = this.getTokens();
    if (!tokens || !tokens.expiresAt) return 0;
    return Math.max(0, tokens.expiresAt - Date.now());
  },

  shouldRefresh(): boolean {
    const tokens = this.getTokens();
    if (!tokens || !tokens.expiresAt) return false;
    
    // Refresh if less than 10 minutes until expiration
    const tenMinutes = 10 * 60 * 1000;
    return (tokens.expiresAt - Date.now()) < tenMinutes;
  },
};

/**
 * User data management (from DynamoDB)
 */
export const userManager = {
  saveUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  getUser(): User | null {
    const user = localStorage.getItem(USER_KEY);
    if (!user) return null;
    try {
      return JSON.parse(user);
    } catch {
      // Corrupt or legacy-format user data — discard rather than crash on boot.
      localStorage.removeItem(USER_KEY);
      return null;
    }
  },

  clearUser(): void {
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Refresh user data from the backend
   * Returns updated user or null if refresh fails
   */
  async refreshUser(apiUrl: string): Promise<User | null> {
    try {
      const token = tokenManager.getIdToken();
      if (!token) return null;

      const response = await fetch(`${apiUrl}/api/profile`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) return null;

      const data = await response.json();
      if (data.user) {
        this.saveUser(data.user);
        return data.user;
      }

      return null;
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      return null;
    }
  },
};

